import React, { useEffect, useState } from 'react'

import {
  formatCurrency,
  formatDateTime,
  formatNumber,
  getTimezoneDate,
} from '../../../../utilities/helpers'

import Badge from 'react-bootstrap/Badge'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'

import {
  closedAvailabilityPeriod,
  offerHasCreatedInventory,
} from '../../offerAvailability'
import { SpecialtyOffer } from '../SpecialtyOffer'

export default function Offer({
  offer,
  offers,
  timezone,
  isEventOnsale,
  isEventPublished,
  isStandard,
  openInventory,
  holds,
  kills,
  tickets,
  handleClick,
  getPrice,
  getStatus,
  getSeatCount,
  getAvailableSeatCount,
}) {
  const [status, setStatus] = useState()

  const [saleStarts, setSaleStarts] = useState()

  const [saleEnds, setSaleEnds] = useState()

  const [minPrice, setMinPrice] = useState(0)

  const [maxPrice, setMaxPrice] = useState(0)

  useEffect(() => {
    const saleStarts = getSaleDate(offers, offer, 'starts')
    setSaleStarts(saleStarts)
    const saleEnds = getSaleDate(offers, offer, 'ends')
    setSaleEnds(saleEnds)
    const activePeriod = {
      starts: saleStarts,
      ends: saleEnds,
    }

    // get status of offer
    // get status if event is changed to onsale
    setStatus(getStatus(activePeriod, offer))
  }, [offers, offer, openInventory, tickets, isEventOnsale, isEventPublished])

  useEffect(() => {
    setMinPrice(
      Math.min(
        ...Object.values(offer?.am_pricing_objects)?.map((_, idx, levels) =>
          getPrice(levels, idx, offer?.pricing_option, offer?.discount)
        )
      )
    )
    setMaxPrice(
      Math.max(
        ...Object.values(offer?.am_pricing_objects)?.map((_, idx, levels) =>
          getPrice(levels, idx, offer?.pricing_option, offer?.discount)
        )
      )
    )
  }, [offer])

  const displayMinPrice = offer?.freeOffer ? 0 : minPrice
  const displayMaxPrice = offer?.freeOffer ? 0 : maxPrice

  // if offer pulls from open inventory
  const hasOpenInventory = (offer) => {
    return offer?.inventoryType.toLowerCase() === 'open'
  }

  // if offers pulled from open inventory are complete - check price level seats
  // if custom offers have inventory
  const hasInventory = (offer) =>
    offerHasCreatedInventory(offer, { openInventory, holds, kills, tickets })

  const getSaleDate = (offers, offer, property) => {
    if (!offer?.availability?.length) return

    const selectedPeriod =
      offer.availability.find((period) => period.active) ||
      closedAvailabilityPeriod(offer.availability)
    if (!selectedPeriod) return

    if (selectedPeriod?.name?.includes('General On-sale')) {
      const standardAdmissionAvailability = offers?.[0]?.availability
      if (!standardAdmissionAvailability?.length) {
        return selectedPeriod?.[property]
      }

      let standardAdmissionPeriod = standardAdmissionAvailability.find(
        (period) => period.name === selectedPeriod.name
      )

      if (!standardAdmissionPeriod) {
        const generalOnsalePeriods = offer.availability.filter((period) =>
          period?.name?.includes('General On-sale')
        )
        const periodIndex = generalOnsalePeriods.findIndex(
          (period) => period === selectedPeriod
        )
        if (periodIndex >= 0) {
          const standardGeneralOnsalePeriods =
            standardAdmissionAvailability.filter((period) =>
              period?.name?.includes('General On-sale')
            )
          standardAdmissionPeriod =
            standardGeneralOnsalePeriods[periodIndex] ??
            standardAdmissionAvailability[periodIndex]
        }
      }

      return standardAdmissionPeriod?.[property] ?? selectedPeriod?.[property]
    }

    return selectedPeriod[property]
  }

  return (
    <>
      <Button
        variant="default"
        as="li"
        className="list-item list-item-lg list-item--light btn-list-view btn-list-view-with-border btn-list-view-with-bg offer-row"
        onClick={(e) =>
          handleClick(e, offer?.id, { ...offer, status: status?.type })
        }
      >
        <Stack direction="horizontal" gap={2} className="col-7">
          <Stack gap={1}>
            <h2 className="heading mb-2">{offer?.name}</h2>
            <Stack direction="horizontal" gap={2}>
              <Badge bg="none" className={`badge--${status?.type} `}>
                {status?.label}
              </Badge>
              <span className="subtitle m-0">
                <span>
                  {formatDateTime(
                    getTimezoneDate(saleStarts, timezone),
                    'dateOnly'
                  )}
                  <span className="time">
                    {formatDateTime(
                      getTimezoneDate(saleStarts, timezone),
                      'timeOnly'
                    )}
                  </span>
                </span>
                <span className="to">
                  {formatDateTime(
                    getTimezoneDate(saleEnds, timezone),
                    'dateOnly'
                  )}
                  <span className="time">
                    {formatDateTime(
                      getTimezoneDate(saleEnds, timezone),
                      'timeOnly'
                    )}
                  </span>
                </span>
              </span>
            </Stack>
            {!hasInventory(offer) && (
              <div className="info-text info-text-md info-text-danger fw-normal">
                <span className="small">{'No inventory created'}</span>
              </div>
            )}
          </Stack>
        </Stack>
        <Stack gap={2} className="col justify-content-center">
          <span className="normal normal-bold">
            {isStandard ? 'Base' : 'Offset'} Price:{' '}
            {formatCurrency(displayMinPrice)}
            <span className="to">{formatCurrency(displayMaxPrice)}</span>
          </span>
          <span className="fw-normal caption">
            {hasOpenInventory(offer) ? 'Open' : 'Exclusive'} (
            {formatNumber(
              getAvailableSeatCount(
                hasOpenInventory(offer) ? openInventory : offer
              )
            )}
            )
          </span>
        </Stack>
      </Button>
      {offer?.connected_offers.map((offer, i) => (
        <SpecialtyOffer
          key={i}
          offer={offer}
          status={status}
          isStandard={isStandard}
          getPrice={getPrice}
          handleClick={handleClick}
        />
      ))}
    </>
  )
}
